import styled from '@emotion/styled';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { TypeBlogPostSkeleton, TypeMicrocopySetSkeleton } from '../../../@types/generated';
import ListItemCard from '../../common-components/component-library/ListItemCard';
import SectionTitle from '../../common-components/component-library/SectionTitle';
import PrimaryButton from '../../common-components/component-library/buttons/PrimaryButton';
import FeatureHero from '../../common-components/component-library/heroes/FeatureHero';
import Container from '../../common-components/component-library/layout/Container';
import { getReadTime } from '../../domains/articles/articleUtils';
import { getEntries } from '../../utils/api/contentful';
import { formatMicrocopy } from '../../utils/contentfulMicrocopy';
import { LOCALES } from '../../utils/locale';
import { spacing } from '../../utils/styleguide';
import withAppProps from '../../utils/withAppProps';

const ArticleListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]}px;
  margin-bottom: ${spacing[4]}px;
  margin-top: ${spacing[6]}px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing[10]}px;
`;

function ArticlesLandingPage({ articles, currentPage, hasNextPage, microcopy }: Props) {
  const { replace } = useRouter();
  const pathname = usePathname();

  function handleLoadMore() {
    if (hasNextPage) {
      const params = new URLSearchParams();
      params.set('page', (currentPage + 1).toString());
      replace(`${pathname}?${params.toString()}`);
    }
  }

  const [latestArticle, ...restArticles] = articles ?? [];

  if (!latestArticle) return null;

  return (
    <>
      <FeatureHero
        sectionTitle={latestArticle.tag}
        paragraph={latestArticle.title}
        bodyText={latestArticle.description}
        imageUrl={latestArticle.image?.url}
        primaryButtonLabel={microcopy.primaryCTALabel}
        primaryButtonHref={`/articles/${latestArticle.slug}`}
        secondaryButtonLabel={microcopy.secondaryCTALabel}
        secondaryButtonHref={microcopy.secondaryCTALink}
      />
      <Container>
        <SectionTitle title="Articles" />
        <ArticleListWrapper>
          {restArticles?.map((article, index) => (
            <ListItemCard
              key={index}
              item={{ ...article, profiles: article.authors, type: 'Article' }}
            />
          ))}
        </ArticleListWrapper>

        <ButtonWrapper>
          {hasNextPage && (
            <PrimaryButton label={microcopy.loadMoreLabel} onClick={handleLoadMore} />
          )}
        </ButtonWrapper>
      </Container>
    </>
  );
}

type Article = {
  tag?: string;
  title?: string;
  description?: string;
  image?: {
    url: string;
    alt: string;
    width: number;
    height: number;
  };
  slug: string;
  authors: string;
  publishedAt: string;
  duration: number;
  url: string;
  externalLink?: string;
};

type Props = {
  articles?: Article[];
  currentPage: number;
  hasNextPage: boolean;
  microcopy: Record<string, string>;
};

export const getServerSideProps = withAppProps<Props>(async ({ locale, query }) => {
  const currentPage = query?.page ? Number(query.page) : 1;
  const pageSize = 7;
  const articlesToFetch = currentPage * pageSize;

  const blogPostQuery = {
    content_type: 'blogPost',
    'fields.availableFor': 'Main web',
    order: '-fields.publishedAt',
    limit: articlesToFetch,
  } as const;

  const [{ items: articles, total }, microCopySet] = await Promise.all([
    getEntries<TypeBlogPostSkeleton>(blogPostQuery),
    getEntries<TypeMicrocopySetSkeleton>({
      content_type: 'microcopySet',
      locale: LOCALES[locale ?? 'en'],
    }),
  ]);

  const { articlesPage } = formatMicrocopy(microCopySet);

  const hasNextPage = articles.length < total;

  return {
    props: {
      articles: articles.map((article) => ({
        tag: article.fields.tag,
        title: article.fields.title,
        description: article.fields.preamble,
        image: article.fields.image
          ? {
              url: article.fields.image?.fields.file?.url ?? '',
              alt: article.fields.image?.fields.title ?? article.fields.title ?? '',
              width: article.fields.image?.fields.file?.details?.image?.width ?? 0,
              height: article.fields.image?.fields.file?.details?.image?.height ?? 0,
            }
          : undefined,
        slug: article.fields.slug,
        authors:
          article.fields.authorProfiles
            ?.filter((profile) => !!profile)
            .map((profile) => profile.fields.name)
            .join(', ') ??
          article.fields.author ??
          '',
        publishedAt: article.fields.publishedAt,
        duration: getReadTime({
          body: article.fields.body?.data?.content,
          title: article.fields.title,
          preamble: article.fields.preamble,
        }),
        url: `/articles/${article.fields.slug}`,
        externalLink: article.fields.externalLink,
      })),
      currentPage,
      hasNextPage,
      microcopy: articlesPage,
    },
    pageAppProps: {
      metaHeader: {
        canonicalLink: 'https://www.milkywire.com/articles',
        seoDescription: 'Read our articles on nature, climate and planet health.',
        seoTitle: 'Articles | Milkywire',
        socialDescription: 'Read our articles on nature, climate and planet health.',
        socialImageHeight: 0,
        socialImageUrl: '',
        socialImageWidth: 0,
        socialTitle: 'Articles | Milkywire',
      },
    },
  };
});

ArticlesLandingPage.layoutProps = {
  initiallyTransparentHeader: true,
  floatingHeader: true,
};

export default ArticlesLandingPage;
